import * as React from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, TextField, Checkbox,
  InputAdornment,
  IconButton,
  Button,
  Box,
  Typography
} from '@mui/material';
import { AddShoppingCart, CheckCircle, Search } from '@mui/icons-material';
import {
  searchProducts,
  tokenizeSearchText,
  productMatchesSearchTokens,
} from '../../services/productSearch';

export default function PickProduct({
  products,
  selectedProducts,
  setSelectedProducts,
  handleAddToCart,
  excludedProductIds,
  supplierId,
  allProducts = false,
}) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [focusedRowIndex, setFocusedRowIndex] = React.useState(0);
  const [remoteProducts, setRemoteProducts] = React.useState([]);
  const [remoteLoading, setRemoteLoading] = React.useState(false);

  React.useEffect(() => {
    if (!search) {
      setDebouncedSearch('');
      return;
    }
    const handle = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handle);
  }, [search]);

  const searchTokens = React.useMemo(() => {
    return tokenizeSearchText(debouncedSearch);
  }, [debouncedSearch]);

  React.useEffect(() => {
    if (!allProducts) {
      setRemoteProducts([]);
      setRemoteLoading(false);
      return undefined;
    }

    const query = String(debouncedSearch || '').trim();
    if (query.length < 2) {
      setRemoteProducts([]);
      setRemoteLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    let isMounted = true;

    setRemoteLoading(true);
    searchProducts({
      q: query,
      supplierId,
      allProducts: true,
      signal: controller.signal,
    })
      .then((items) => {
        if (!isMounted) return;
        const rows = Array.isArray(items) ? items.slice(0, 5) : [];
        setRemoteProducts(rows);
      })
      .catch((error) => {
        if (!isMounted) return;
        if (error?.name === 'CanceledError' || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
          return;
        }
        setRemoteProducts([]);
      })
      .finally(() => {
        if (isMounted) setRemoteLoading(false);
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [allProducts, debouncedSearch, supplierId]);

  const excludedSet = React.useMemo(() => {
    if (!excludedProductIds) return new Set();
    if (excludedProductIds instanceof Set) return excludedProductIds;
    return new Set(excludedProductIds);
  }, [excludedProductIds]);

  const sourceProducts = React.useMemo(() => {
    if (allProducts) return remoteProducts;
    return products || [];
  }, [allProducts, products, remoteProducts]);

  const filteredProducts = React.useMemo(() => {
    const hasMinQuery = String(debouncedSearch || '').trim().length >= 2;
    if (allProducts && !hasMinQuery) return [];

    const rows = sourceProducts.filter((product) => (
      !product?.isAdd &&
      !excludedSet.has(product?.id) &&
      productMatchesSearchTokens(product, searchTokens)
    ));

    return allProducts ? rows.slice(0, 5) : rows;
  }, [allProducts, debouncedSearch, excludedSet, searchTokens, sourceProducts]);

  const selectedIds = React.useMemo(() => new Set(selectedProducts.map((p) => p.id)), [selectedProducts]);
  const filteredIds = React.useMemo(() => new Set(filteredProducts.map((p) => p.id)), [filteredProducts]);
  const isAllFilteredSelected = React.useMemo(() => (
    filteredProducts.length > 0 && filteredProducts.every((p) => selectedIds.has(p.id))
  ), [filteredProducts, selectedIds]);

  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectProduct = (product) => {
    setSelectedProducts(prev => {
      const isSelected = prev.some(p => p.id === product.id);
      return isSelected ? prev.filter(p => p.id !== product.id) : [...prev, product];
    });
  };

  const handleSelectAll = React.useCallback(() => {
    if (filteredProducts.length === 0) return;
    setSelectedProducts((prev) => {
      const prevIds = new Set(prev.map((p) => p.id));
      if (filteredProducts.every((p) => prevIds.has(p.id))) {
        return prev.filter((p) => !filteredIds.has(p.id));
      }
      const next = [...prev];
      filteredProducts.forEach((p) => {
        if (!prevIds.has(p.id)) {
          next.push(p);
          prevIds.add(p.id);
        }
      });
      return next;
    });
  }, [filteredProducts, filteredIds, setSelectedProducts]);

  const handleRowClick = (product) => (event) => {
    if (event.target.type !== 'checkbox') {
      handleSelectProduct(product);
    }
  };

  const handleKeyDown = (event) => {
    const { key } = event;
    const rowCount = filteredProducts.length;

    if (key === 'ArrowDown') {
      setFocusedRowIndex((prevIndex) => Math.min(prevIndex + 1, rowCount - 1));
    } else if (key === 'ArrowUp') {
      setFocusedRowIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    } else if (key === 'Enter') {
      const focusedProduct = filteredProducts[focusedRowIndex];
      if (focusedProduct) {
        handleSelectProduct(focusedProduct);
      }
    }
  };
  const handleAddToCartLocal = () => {
    handleAddToCart();
  };

  React.useEffect(() => {
    const rowElement = document.getElementById(`row-${focusedRowIndex}`);
    if (rowElement) {
      rowElement.focus();
    }
  }, [focusedRowIndex]);

  React.useEffect(() => {
    setPage(0);
    setFocusedRowIndex(0);
  }, [debouncedSearch]);

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', padding: 0 }}>

<Box sx={{
  p: 2,
  backgroundColor: 'white',
  borderBottom: '1px solid',
  borderColor: 'divider'
}}>
  {/* Solo el título en móvil si es necesario */}
  <Typography 
    variant="h6" 
    sx={{ 
      display: { xs: 'block', md: 'none' },
      mb: 2,
      fontWeight: 600
    }}
  >
    Agregar productos
  </Typography>

  {/* Contenedor SIMPLE que funciona */}
  <Box sx={{
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: 2,
    alignItems: { xs: 'stretch', md: 'center' }
  }}>
    
    {/* Campo de búsqueda - SIMPLE y FUNCIONAL */}
    <TextField
      placeholder="Buscar productos..."
      variant="outlined"
      size="small"
      fullWidth
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        ),
      }}
      sx={{
        flex: 1,
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'white'
        }
      }}
    />

    {/* Botones - SIMPLES y FUNCIONALES */}
    <Box sx={{
      display: 'flex',
      gap: 1,
      width: { xs: '100%', md: 'auto' }
    }}>
<Box sx={{ position: 'relative' }}>
  <Button
    variant="outlined"
    size="small"
    onClick={handleSelectAll}
    disabled={filteredProducts.length === 0}
    sx={{
      height: 36,
      px: 2,
      fontSize: '0.8125rem',
      fontWeight: 500,
      color: isAllFilteredSelected ? '#4CAF50' : 'grey.700',
      textTransform: 'none',
      borderRadius: 0.75,
      borderColor: isAllFilteredSelected ? '#4CAF50' : 'grey.300',
      backgroundColor: 'white',
      minWidth: 140,
      transition: 'all 0.2s ease',
      position: 'relative',
      zIndex: 1,
      '&:hover': {
        borderColor: isAllFilteredSelected ? '#388E3C' : 'grey.400',
        backgroundColor: isAllFilteredSelected ? 'rgba(76, 175, 80, 0.04)' : 'rgba(0, 0, 0, 0.02)',
        transform: 'translateY(-1px)',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      },
      '&:active': {
        transform: 'translateY(0)'
      },
      '&:disabled': {
        color: 'grey.400',
        borderColor: 'grey.200',
        backgroundColor: 'grey.50',
        transform: 'none',
        boxShadow: 'none'
      }
    }}
  >
    {isAllFilteredSelected ? 'Deseleccionar todo' : 'Seleccionar todo'}
  </Button>
  
  {/* Efecto sutil de check cuando todo está seleccionado */}
  {isAllFilteredSelected && (
    <CheckCircle
      sx={{
        position: 'absolute',
        top: -4,
        right: -4,
        fontSize: 16,
        color: '#4CAF50',
        backgroundColor: 'white',
        borderRadius: '50%',
        padding: 0.5,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        zIndex: 2
      }}
    />
  )}
</Box>

      <Box sx={{ position: 'relative' }}>
        <IconButton
          size="small"
          onClick={handleAddToCartLocal}
          disabled={selectedProducts.length === 0}
          sx={{
            height: 34,
            width: 34,
            backgroundColor: selectedProducts.length > 0 ? 'success.main' : 'transparent',
            color: selectedProducts.length > 0 ? 'white' : 'grey.600',
            border: '1px solid',
            borderColor: selectedProducts.length > 0 ? 'success.main' : 'grey.300',
            '&:hover': selectedProducts.length > 0 ? {
              backgroundColor: 'success.dark',
              borderColor: 'success.dark'
            } : {
              backgroundColor: 'grey.100'
            },
            '&:disabled': {
              borderColor: 'grey.200',
              color: 'grey.400'
            }
          }}
        >
          <AddShoppingCart fontSize="small" />
        </IconButton>

        {/* Badge minimalista */}
        {selectedProducts.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: -5,
              right: -5,
              minWidth: 18,
              height: 18,
              backgroundColor: 'white',
              color: 'success.dark',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.65rem',
              fontWeight: 700,
              border: '1.5px solid',
              borderColor: 'success.main',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
          >
            {selectedProducts.length}
          </Box>
        )}
      </Box>
    </Box>
  </Box>
  {allProducts && String(search || '').trim().length < 2 && (
    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
      Escribe para buscar (min. 2 caracteres)
    </Typography>
  )}
  {allProducts && remoteLoading && (
    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
      Buscando...
    </Typography>
  )}
</Box>


      <TableContainer>
        <Table
          sx={{ minWidth: 650 }}
          size="small"
          aria-label="tabla de productos"
          onKeyDown={handleKeyDown}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">Descripción</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Empresa</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Unidades</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProducts
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => (
                <TableRow
                  key={row.id}
                  id={`row-${index}`}
                  onClick={handleRowClick(row)}
                  tabIndex={0}
                  sx={{
                    '&:hover': { backgroundColor: '#f5f5f5', cursor: 'pointer' },
                    backgroundColor: focusedRowIndex === index ? '#e0e0e0' : 'inherit',
                  }}
                >
                  <TableCell>
                    <Checkbox
                      size="small"
                      checked={selectedProducts.some(p => p.id === row.id)}
                      onChange={() => handleSelectProduct(row)}
                      inputProps={{
                        'aria-label': selectedProducts.some(p => p.id === row.id) ? 'Deseleccionar producto' : 'Seleccionar producto',
                      }}
                    />
                  </TableCell>
                  <TableCell align="left">{row.description}</TableCell>
                  <TableCell align="right">{row.supplier.companyName}</TableCell>
                  <TableCell align="right">{row.units}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 15]}
        component="div"
        count={filteredProducts.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </Paper>
  );
}
